import { Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import { useEffect, useMemo, useState } from "react";
import { getRowSelectionModelFromIds } from "@entrilia/shared/components/grid/gridHelper.ts";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import type { ChartOfAccountsItem } from "../../../../../../../../api/types/svTypes.ts";
import { AccountType, MeasureUnitTable } from "../../../../../../../../api/types/svTypes.ts";
import { useChartOfAccountsContext } from "./contexts/ChartOfAccountsContext";

interface LedgerAccountsListProps {
  chartOfAccounts: ChartOfAccountsItem[] | undefined;
  ledger: MeasureUnitTable;
}

const blinkKeyframes = {
  "@keyframes blinking": {
    "50%": { boxShadow: "0 0 5px #00A866" },
  },
};

const getRowId = (row: ChartOfAccountsItem) => row.accountNo;

export default function LedgerAccountsList({ chartOfAccounts, ledger }: LedgerAccountsListProps) {
  const rowHeight = 32;
  const { selectedAccounts, scrollTargetAccount } = useChartOfAccountsContext();
  const [activeAccountScroll, setActiveAccountScroll] = useState<{ accountNo: string }>();
  const apiRef = useGridApiRef();

  useEffect(() => {
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setActiveAccountScroll(scrollTargetAccount);
  }, [scrollTargetAccount]);

  const columns = useMemo(() => {
    const result: GridColDef<ChartOfAccountsItem>[] = [];
    if (chartOfAccounts) {
      result.push({ field: "no", headerName: "No.", width: 100, valueGetter: (_, row) => row.accountNo });
      result.push({
        field: "name",
        headerName: "Name",
        minWidth: 70,
        flex: 1,
        valueGetter: (_, row) => row.accountName,
        renderCell(params) {
          return (
            <Typography variant={getNameCellVariant(params.row.accountType)} sx={{ pl: params.row.level }}>
              {params.value}
            </Typography>
          );
        },
      });
      if (ledger === MeasureUnitTable.Gl) {
        result.push({
          field: "ib",
          headerName: "Income/Balance",
          minWidth: 70,
          flex: 0.8,
          valueGetter: (_, row) => row.incomeBalance,
        });
      }
      result.push({
        field: "acn",
        headerName: "Account Category Name",
        minWidth: 70,
        flex: 0.8,
        valueGetter: (_, row) => row.accountCategory,
      });
      result.push({
        field: "as",
        headerName: "Account Subcategories",
        minWidth: 70,
        flex: 0.8,
        valueGetter: (_, row) => row.accountSubcategory,
      });
      result.push({
        field: "at",
        headerName: "Account Type",
        width: 110,
        valueGetter: (_, row) => row.accountType,
      });
    }
    return result.map((col) => ({ ...col, sortable: false }));
  }, [chartOfAccounts, ledger]);

  useEffect(() => {
    const current = apiRef.current;
    if (!current || !scrollTargetAccount) {
      return;
    }
    const hasAccount = chartOfAccounts?.some((account) => scrollTargetAccount.accountNo === account.accountNo);
    if (!hasAccount) {
      return;
    }

    const scrollTimeoutId = setTimeout(() => {
      current.scrollToIndexes({
        rowIndex: current.getRowIndexRelativeToVisibleRows(scrollTargetAccount.accountNo),
      });
    }, 10);

    const resetTimeoutId = setTimeout(() => {
      setActiveAccountScroll((prev) => (prev?.accountNo === scrollTargetAccount.accountNo ? undefined : prev));
    }, 500);

    return () => {
      clearTimeout(scrollTimeoutId);
      clearTimeout(resetTimeoutId);
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [scrollTargetAccount]);

  return (
    <Stack
      spacing={2}
      useFlexGap
      sx={{
        py: 2.5,
        pr: 3,
        pl: 1.5,
        width: "100%",
        ...blinkKeyframes,
        "& .animated-item": { animation: "blinking .15s steps(4) 4" },
      }}
    >
      <ScrollableFlexContainer>
        <DataGridPremium
          apiRef={apiRef}
          rowHeight={rowHeight}
          columnHeaderHeight={32}
          columns={columns}
          rowSelectionModel={getRowSelectionModelFromIds(selectedAccounts)}
          rows={chartOfAccounts ?? []}
          getRowId={getRowId}
          getRowClassName={(params) => {
            if (params.id === activeAccountScroll?.accountNo) {
              return "animated-item";
            }
            return "";
          }}
          disableRowSelectionOnClick
          disableColumnSelector
          disableColumnMenu
          hideFooter
        ></DataGridPremium>
      </ScrollableFlexContainer>
    </Stack>
  );
}

const getNameCellVariant = (type: AccountType) => {
  switch (type) {
    case AccountType.Heading:
    case AccountType.BeginTotal:
    case AccountType.Total:
    case AccountType.EndTotal:
      return "subtitle2";
    default:
      return "body1";
  }
};
